import { useState, useEffect, useCallback, useRef } from "react";
import { useProfile } from "../../../hooks/useProfile";
import { apiFetch } from "../../../context/AuthContext";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const useDocumentRequests = (language, showAlert) => {
  const { profile } = useProfile();

  // ─── Requests State ────────────────────────────────────────────────────────
  const [pendingRequests, setPendingRequests] = useState([]);
  const [completedRequests, setCompletedRequests] = useState([]);
  // Starts true (not false) so the page-level skeleton is guaranteed to be
  // visible on first paint instead of racing the mount effect that flips it.
  const [isLoading, setIsLoading] = useState(true);

  // ─── Modal State ───────────────────────────────────────────────────────────
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showProcessModal, setShowProcessModal] = useState(false);
  const [comments, setComments] = useState("");

  // ─── Duplicate Confirm State ───────────────────────────────────────────────
  const [showDuplicateConfirm, setShowDuplicateConfirm] = useState(false);
  const [duplicateExistingDoc, setDuplicateExistingDoc] = useState(null);
  const [pendingApproveId, setPendingApproveId] = useState(null);

  // ─── Permissions State ─────────────────────────────────────────────────────
  const [documentFeatures, setDocumentFeatures] = useState([]);
  const [userRole, setUserRole] = useState("");
  const [viewType, setViewType] = useState("");

  // Keep a stable ref to profile so callbacks can read the latest values
  // without needing profile in their dependency arrays (which would recreate
  // the functions on every notification-triggered re-render and re-fire the
  // init useEffect, overwriting a just-approved request with stale data).
  const profileRef = useRef(profile);
  useEffect(() => { profileRef.current = profile; }, [profile]);

  const buildFetchUrl = useCallback((status) => {
    const storedViewType = localStorage.getItem("viewType");
    if (storedViewType === "admin") {
      return `${BASE_URL}/api/document-requests/visibility?status=${status}`;
    }
    const clientName =
      profileRef.current?.clientName || localStorage.getItem("clientName");
    const managerCode =
      profileRef.current?.infinityCode || localStorage.getItem("infinityCode");
    return `${BASE_URL}/api/document-requests/all?status=${status}&clientName=${encodeURIComponent(
      clientName || ""
    )}&managerCode=${encodeURIComponent(managerCode || "")}`;
  }, []); // stable — reads profile from ref

  const buildFetchHeaders = useCallback(() => {
    const storedViewType = localStorage.getItem("viewType");
    return {
      "x-view-type": storedViewType || "admin",
    };
  }, []);

  const parseRequests = useCallback((data) => {
    const storedViewType = localStorage.getItem("viewType");
    return storedViewType === "admin"
      ? data.data?.requests || []
      : Array.isArray(data)
        ? data
        : [];
  }, []);

  const fetchPendingRequests = useCallback(async () => {
    try {
      setIsLoading(true);
      const url = buildFetchUrl("pending");
      const response = await apiFetch(url, {
        method: "GET",
        headers: buildFetchHeaders(),
      });
      if (!response.ok) throw new Error("Failed to fetch pending requests");
      const data = await response.json();
      setPendingRequests(parseRequests(data));
    } catch (error) {
      console.error("Failed to fetch requests:", error);
    } finally {
      setIsLoading(false);
    }
  }, [buildFetchHeaders, buildFetchUrl, parseRequests]);

  const fetchCompletedRequests = useCallback(async () => {
    try {
      setIsLoading(true);
      const url = buildFetchUrl("completed,rejected");
      const response = await apiFetch(url, {
        method: "GET",
        headers: buildFetchHeaders(),
      });
      if (!response.ok) throw new Error("Failed to fetch completed requests");
      const data = await response.json();
      const requests = parseRequests(data);

      const oneMonthAgo = new Date();
      oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

      const recentCompleted = requests.filter((req) => {
        const completedDate = new Date(req.processedDate || req.created_at);
        return completedDate >= oneMonthAgo;
      });

      setCompletedRequests(recentCompleted);
    } catch (error) {
      console.error("Failed to fetch completed requests:", error);
    } finally {
      setIsLoading(false);
    }
  }, [buildFetchHeaders, buildFetchUrl, parseRequests]);

  // ─── Init ──────────────────────────────────────────────────────────────────
  // ⚠️ fetchPendingRequests / fetchCompletedRequests are intentionally NOT in
  // the dependency array. They are stable (their own deps are all stable), so
  // adding them would cause a re-fetch on every notification socket re-render,
  // which would overwrite a just-approved request with stale 'pending' data.
  const didInitRef = useRef(false);
  useEffect(() => {
    const storedAdminLevel = localStorage.getItem("adminLevel");
    const storedRoleRaw = profile?.role || localStorage.getItem("role");
    const storedViewType = localStorage.getItem("viewType");

    if (storedRoleRaw) setUserRole(storedRoleRaw.toLowerCase());
    if (storedViewType) setViewType(storedViewType.toLowerCase());

    if (storedAdminLevel) {
      try {
        const parsed = JSON.parse(storedAdminLevel);
        setDocumentFeatures(parsed?.features?.documents || []);
      } catch (error) {
        console.error("Error parsing adminLevel:", error);
      }
    }

    // Only fetch once on mount (not on every profile re-render)
    if (!didInitRef.current) {
      didInitRef.current = true;
      fetchPendingRequests();
      fetchCompletedRequests();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps


  // ─── Notification Target ───────────────────────────────────────────────────
  useEffect(() => {
    const target = window.__notificationTarget;
    if (target?.documentId) {
      const allRequests = [...pendingRequests, ...completedRequests];
      if (allRequests.length > 0) {
        const request = allRequests.find(
          (r) => r.id === Number(target.documentId)
        );
        if (request) {
          setSelectedRequest(request);
          setShowProcessModal(true);
          window.__notificationTarget = null;
        }
      }
    }
  }, [pendingRequests, completedRequests]);

  // ─── Permission Helpers ────────────────────────────────────────────────────
  const isPrivilegedRole = userRole === "admin" || userRole === "superadmin";
  const shouldEnforceDocumentPermissions = viewType === "admin";

  const canApproveDocuments =
    !shouldEnforceDocumentPermissions ||
    isPrivilegedRole ||
    documentFeatures.includes("approve");

  const canRejectDocuments =
    !shouldEnforceDocumentPermissions ||
    isPrivilegedRole ||
    documentFeatures.includes("reject");

  const canDownloadDocuments =
    !shouldEnforceDocumentPermissions ||
    isPrivilegedRole ||
    documentFeatures.includes("download");

  // "view" was never a real registry feature for this module (only
  // "download"/"approve"/"reject" exist) — checking it made Preview
  // permanently unreachable for any promoted Admin View account.
  const canViewDocuments =
    !shouldEnforceDocumentPermissions ||
    isPrivilegedRole ||
    documentFeatures.includes("download");

  const canProcessDocuments = canApproveDocuments || canRejectDocuments;

  // ─── Approve ───────────────────────────────────────────────────────────────
  const handleApprove = async (requestId) => {
    try {
      if (!canApproveDocuments) {
        showAlert(
          "✗ Error",
          "You do not have permission to approve document requests",
          "error"
        );
        return;
      }

      const req =
        pendingRequests.find((r) => r.id === requestId) || selectedRequest;

      if (req?.workflowInstanceId && req.currentUserCanAct === false) {
        showAlert(
          "✗ Error",
          "You are not assigned to approve this document request workflow step",
          "error"
        );
        return;
      }

      if (req?.requestType?.startsWith("upload:")) {
        const docType = req.requestType.replace("upload:", "");
        try {
          const checkRes = await apiFetch(
            `${BASE_URL}/api/documents/check/${req.infinityCode}/${docType}`
          );
          if (checkRes.ok) {
            const checkData = await checkRes.json();
            if (checkData.exists) {
              setDuplicateExistingDoc(checkData.document);
              setPendingApproveId(requestId);
              setShowDuplicateConfirm(true);
              return;
            }
          }
        } catch (_) {
          /* non-blocking */
        }
      }

      await doApprove(requestId);
    } catch (error) {
      console.error("Approval failed:", error);
      showAlert("✗ Error", "Failed to process request", "error");
    }
  };

  const doApprove = async (requestId) => {
    try {
      setIsLoading(true);
      const adminCode =
        profile?.infinityCode || localStorage.getItem("infinityCode");
      const req =
        pendingRequests.find((r) => r.id === requestId) || selectedRequest;

      if (req?.workflowInstanceId && req.currentUserCanAct === false) {
        showAlert(
          "✗ Error",
          "You are not assigned to approve this document request workflow step",
          "error"
        );
        return;
      }

      const res = await apiFetch(
        `${BASE_URL}/api/document-requests/${requestId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            status: "approved",
            processedBy: adminCode,
            comments,
          }),
        }
      );
      if (!res.ok) throw new Error("Approval failed");

      const data = await res.json();

      let successMessage = "Document request processed successfully.";
      if (data.isFullyCompleted) {
        successMessage = "Document request fully approved! Document generated and sent to employee.";
      } else if (data.message) {
        successMessage = `${data.message} (Waiting for next approver)`;
      }

      showAlert("✓ Success", successMessage, "success");
      fetchPendingRequests();
      fetchCompletedRequests();
      setShowProcessModal(false);
      setShowDuplicateConfirm(false);
      setDuplicateExistingDoc(null);
      setPendingApproveId(null);
      setComments("");
    } catch (error) {
      console.error("Approval failed:", error);
      showAlert("✗ Error", "Failed to process request", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // ─── Reject ────────────────────────────────────────────────────────────────
  const handleReject = async (requestId) => {
    try {
      if (!canRejectDocuments) {
        showAlert(
          "✗ Error",
          "You do not have permission to reject document requests",
          "error"
        );
        return;
      }

      const req =
        pendingRequests.find((r) => r.id === requestId) || selectedRequest;

      if (req?.workflowInstanceId && req.currentUserCanAct === false) {
        showAlert(
          "✗ Error",
          "You are not assigned to reject this document request workflow step",
          "error"
        );
        return;
      }

      setIsLoading(true);
      const adminCode =
        profile?.infinityCode || localStorage.getItem("infinityCode");
      const res = await apiFetch(
        `${BASE_URL}/api/document-requests/${requestId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            status: "rejected",
            processedBy: adminCode,
            comments,
          }),
        }
      );
      if (!res.ok) throw new Error("Rejection failed");

      showAlert("✓ Success", "Request rejected successfully", "success");
      fetchPendingRequests();
      setShowProcessModal(false);
      setComments("");
    } catch (error) {
      console.error("Rejection failed:", error);
      showAlert("✗ Error", "Failed to process request", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // ─── Download / Preview ────────────────────────────────────────────────────
  const handleDownload = async (documentId) => {
    try {
      if (!canDownloadDocuments) {
        showAlert(
          "✗ Error",
          "You do not have permission to download documents",
          "error"
        );
        return;
      }

      const response = await apiFetch(
        `${BASE_URL}/api/documents/download/${documentId}`,
        { method: "GET" }
      );

      if (!response.ok) throw new Error("Download failed");

      const contentDisposition = response.headers.get("Content-Disposition");
      let filename = "document.pdf";
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(
          /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
        );
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, "");
        }
      }

      if (!filename.toLowerCase().endsWith(".pdf")) filename += ".pdf";

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
      showAlert("✗ Error", "Failed to download document", "error");
    }
  };

  const handlePreview = (documentId) => {
    if (!canViewDocuments) {
      showAlert(
        "✗ Error",
        "You do not have permission to view documents",
        "error"
      );
      return;
    }

    const token = localStorage.getItem("token");
    const url = `${BASE_URL}/api/documents/preview/${documentId}?token=${encodeURIComponent(token)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // ─── Duplicate Modal Dismiss ───────────────────────────────────────────────
  const dismissDuplicateConfirm = () => {
    setShowDuplicateConfirm(false);
    setDuplicateExistingDoc(null);
    setPendingApproveId(null);
  };

  return {
    // State
    pendingRequests,
    completedRequests,
    isLoading,
    selectedRequest,
    setSelectedRequest,
    showProcessModal,
    setShowProcessModal,
    comments,
    setComments,
    showDuplicateConfirm,
    duplicateExistingDoc,
    pendingApproveId,
    // Permissions
    canApproveDocuments,
    canRejectDocuments,
    canDownloadDocuments,
    canViewDocuments,
    canProcessDocuments,
    // Handlers
    handleApprove,
    doApprove,
    handleReject,
    handleDownload,
    handlePreview,
    dismissDuplicateConfirm,
  };
};

export default useDocumentRequests;